import {useEffect, useMemo, useRef} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Header, Tabs} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import useTabs from '@/components/Tabs/useTabs';
import type {TabItem} from '@/types';

import BusinessTransactions from './BusinessTransactions';
import TransactionFilterBar from './TransactionSearch';
import UploadPoolTransactionButton from './UploadPoolTransactionButton';
import useMerchantTransactionList from './useMerchantTransactionList';

const getTabBusinessLabel = (tabId: string): string => {
  if (tabId === 'swift') {
    return 'Swift Business';
  }

  if (tabId === 'system') {
    return 'System Business';
  }

  return 'All Business';
};

const MerchantTransactionList = () => {
  const {
    error,
    isLoading,
    isSearch,
    isPageReset,
    pageInfo,
    transactionList,
    exportFilters,
    filterCount,
    currentFilters,
    filtersToApply,
    clearFilters,
    selectAppliedFilters,
    selectCurrentFilters,
    handleApplyFilters,
    handleFetchTransactions,
  } = useMerchantTransactionList();

  const transactionTabs: TabItem[] = useMemo(
    () => [
      {
        id: 'all',
        dataCy: 'merchant-transactions-tab-all',
        title: 'All Transactions',
        content: (
          <BusinessTransactions
            error={error}
            isLoading={isLoading}
            isSearch={isSearch}
            isPageReset={isPageReset}
            pageInfo={pageInfo}
            transactionList={transactionList}
            handleFetchTransactions={handleFetchTransactions}
          />
        ),
      },
      {
        id: 'swift',
        dataCy: 'merchant-transactions-tab-swift',
        title: 'Swift Transactions',
        content: (
          <BusinessTransactions
            error={error}
            isLoading={isLoading}
            isSearch={isSearch}
            isPageReset={isPageReset}
            pageInfo={pageInfo}
            transactionList={transactionList}
            handleFetchTransactions={handleFetchTransactions}
          />
        ),
      },
      {
        id: 'system',
        dataCy: 'merchant-transactions-tab-system',
        title: 'System Transactions',
        content: (
          <BusinessTransactions
            error={error}
            isLoading={isLoading}
            isSearch={isSearch}
            isPageReset={isPageReset}
            pageInfo={pageInfo}
            transactionList={transactionList}
            handleFetchTransactions={handleFetchTransactions}
          />
        ),
      },
      {
        id: 'pool',
        dataCy: 'merchant-transactions-tab-pool',
        title: 'Pool Transactions',
        content: (
          <BusinessTransactions
            error={error}
            isLoading={isLoading}
            isSearch={isSearch}
            isPageReset={isPageReset}
            pageInfo={pageInfo}
            transactionList={transactionList}
            handleFetchTransactions={handleFetchTransactions}
            actionButton={
              <PermissionsProvider
                permission={AfriexAdminPermission.BusinessPoolDepositReview}
              >
                <UploadPoolTransactionButton />
              </PermissionsProvider>
            }
          />
        ),
      },
    ],
    [
      error,
      isLoading,
      isSearch,
      isPageReset,
      pageInfo,
      transactionList,
      handleFetchTransactions,
    ],
  );

  const {currentTab, handleTabChange} = useTabs(transactionTabs, 'tab');

  // The tab the filters belong to. Filters are cleared when the admin moves to
  // another tab, but not on mount: a list reopened with Back keeps the
  // filters restored from the URL.
  const filteredTabRef = useRef(currentTab.id);
  useEffect(() => {
    if (filteredTabRef.current !== currentTab.id) {
      filteredTabRef.current = currentTab.id;
      clearFilters();
    }

    if (currentTab.id === 'swift') {
      handleFetchTransactions('swift');
    } else if (currentTab.id === 'system') {
      handleFetchTransactions('system');
    } else if (currentTab.id === 'pool') {
      handleFetchTransactions('pool');
    } else {
      handleFetchTransactions('all');
    }
  }, [currentTab.id, handleFetchTransactions, clearFilters]);

  const countSuffix =
    pageInfo.total > 0 ? ` (${pageInfo.total?.toLocaleString()})` : '';
  const tabBusinessLabel = getTabBusinessLabel(currentTab.id);
  return (
    <main className="mt-10 mb-5 mx-4 md:mx-10">
      <Header
        title={`${tabBusinessLabel} Transactions${countSuffix}`}
        dataCy="merchant-transactions-heading"
      />
      <TransactionFilterBar
        transactions={transactionList}
        exportFilters={exportFilters}
        filterCount={filterCount}
        currentFilters={currentFilters}
        filtersToApply={filtersToApply}
        clearFilters={clearFilters}
        selectCurrentFilters={selectCurrentFilters}
        selectAppliedFilters={selectAppliedFilters}
        handleApplyFilters={handleApplyFilters}
        disableAdvancedFilters={
          currentTab.id === 'swift' || currentTab.id === 'system'
        }
        currentTabId={(currentTab?.id as 'swift' | 'system' | 'all') || 'all'}
      />
      <div className="mt-5">
        <Tabs
          tabs={transactionTabs}
          activeTabId={currentTab.id}
          onTabChange={handleTabChange}
        />
      </div>
    </main>
  );
};

export default MerchantTransactionList;
